import { Box, Button, Divider, Stack, Typography, useTheme } from "@mui/material";
import { FoodsIcon, SectionTab, SectionTabs } from "@foodpilot/foods";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import QuestionnaireIcon from "src/assets/icons/questionnaire.svg?react";
import ScoresIcon from "src/assets/icons/scores.svg?react";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { CampaignSelector } from "src/components/Selector";
import { useClientSwitch } from "src/components/ClientSwitch/useClientSwitch";
import { useApplicationParameters } from "src/api/client-api/appParams";
// import { Questionnaire } from "./Questionnaire/Questionnaire";
import { Header } from "./Header";
import { URLHandler } from "src/services/URLHandler";
import { getApiUrl } from "src/api/axiosClient/utils";
import { useGetCompanyPost } from "src/api/client-api/post/useGetCompanyPost";
import { useCompanyMetadata } from "src/context/Company/Metadata/useCompanyMetadata";
import { useGetOneSsq } from "src/api/sure-api/ssq/useGetOneSsq";
import { ExportSSQExcelButton } from "src/components/Button/ExportExcelSSQButton";

type Sections = "scores" | "questionnaire" | "exigences" | "synthese" | "comparison";
export type CompanyTabsProps = {
    section: Sections;
};

type CompanySectionTab = SectionTab & { title?: string };
type CompanySections = Partial<Record<Sections, CompanySectionTab>>;

export const CompanyTabs = (props: CompanyTabsProps) => {
    const { section } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate();
    const clientSwitch = useClientSwitch();
    const { isFeatureActivated } = useApplicationParameters();
    const { scoreId } = useParams();
    const { company } = useCompanyMetadata();
    const { currentCampaign, selectedCompany } = useFoodpilotContext();
    const { post } = useGetCompanyPost({ companyId: company?.id, campaignId: currentCampaign?.id });

    const ssqId = company?.companySector?.ssqId;
    const { ssq } = useGetOneSsq(ssqId);

    if (!selectedCompany) return <Typography variant="big-bold">{t("error.wrongCompany")}</Typography>;

    const collective = {
        scores: {
            name: t("Scores & indicateurs clés"),
            title: t("Indicateurs de l'entreprise"),
            icon: <ScoresIcon width="22px" />,
            onClick: () => {
                const url = URLHandler.company.scores(selectedCompany.id);
                navigate(url);
            },
        },
        questionnaire: {
            name: t("Questionnaire"),
            title: t("Questionnaire"),
            icon: <QuestionnaireIcon width="16px" />,
            onClick: () => {
                const url = URLHandler.company.ssq(selectedCompany.id);
                navigate(url);
            },
        },
    };

    // Create legal requirements tab based on feature switch
    const legalRequirementsTab =
        isFeatureActivated("FS-legal-requirement") ?
            {
                exigences: {
                    name: t("bred.requirement"),
                    title: t("bred.requirement.title"),
                    icon: <FoodsIcon icon="rules" size={3} />,
                    onClick: () => {
                        const url = URLHandler.company.requirements(selectedCompany.id);
                        navigate(url);
                    },
                },
            }
        :   {};

    // const synthesisTab =
    //     isFeatureActivated("FS-legal-requirement") ?
    //         {
    //             synthese: {
    //                 name: t("bred.synthesis"),
    //                 title: t("bred.synthesis.title", { year: currentCampaign?.label }),
    //                 icon: <FoodsIcon icon="scoresIndicators" size={3} />,
    //                 onClick: () => {
    //                     const url = URLHandler.company.synthesis(selectedCompany.id);
    //                     navigate(url);
    //                 },
    //             },
    //         }
    //     :   {};

    // Get sections based on client type
    const sections = clientSwitch<CompanySections>({
        defaultOption: {},
        collective: { ...collective, ...legalRequirementsTab },
        foodpilotLustucru: { ...collective, ...legalRequirementsTab },
        // bred: {
        //     ...legalRequirementsTab,
        //     synthese: {
        //         name: t("bred.synthesis"),
        //         title: t("bred.synthesis.title", { year: currentCampaign?.label }),
        //         icon: <FoodsIcon icon="scoresIndicators" size={3} />,
        //         onClick: () => {
        //             const url = URLHandler.company.synthesis(selectedCompany.id);
        //             navigate(url);
        //         },
        //     },
        //     questionnaire: {
        //         name: t("Questionnaire"),
        //         title: t("bred.questionnaire.title", { year: currentCampaign?.label }),
        //         icon: <FoodsIcon icon="questionnaires" size={3} />,
        //         component: <Questionnaire key="questionnaire-bred" />,
        //         onClick: () => {
        //             const url = URLHandler.company.ssq(selectedCompany.id);
        //             navigate(url);
        //         },
        //     },
        //     // Legal requirements tab is now conditionally included via legalRequirementsTab
        // },
    });

    const isPdfActivated = isFeatureActivated("FS-collective-pdf-export");
    const isAssesmentPdfActivated = isFeatureActivated("FS-collective-assessment-pdf-export");

    const exportPdfDisabled = !(post?.scores?.[13]?.score !== 0 && post?.scores?.[13]?.variations.length !== 0);
    const dynamicPdfExportUri = ssq && post ? `${getApiUrl()}/api/ssq_posts/${post.id}/pdf-report` : "";

    const ssqProgression = post?.completions.ssq.result ?? 0;

    return (
        <Box component="div" sx={{ marginLeft: "auto", marginRight: "auto" }}>
            <Box
                sx={{
                    width: "auto",
                    background: "#fff",
                    padding: "28px 52px",
                    paddingBottom: 0,
                    position: "relative",
                    borderBottom: `1px solid ${theme.custom.grey[500]}`,
                }}
            >
                <Stack
                    direction="row"
                    alignItems="center"
                    gap="16px"
                    sx={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center" }}
                >
                    <Header />
                    <CampaignSelector />
                </Stack>

                <Divider sx={{ marginTop: "28px" }} />
                <SectionTabs
                    tabs={Object.values(sections)}
                    value={Object.keys(sections).indexOf(section)}
                    renderComponents={false}
                    sx={{
                        position: "relative",
                        bottom: "-1px",
                    }}
                />
            </Box>

            <Stack gap="28px" sx={{ padding: "40px 52px" }}>
                {scoreId === undefined && (
                    <Stack
                        justifyContent="space-between"
                        gap="16px"
                        sx={{
                            [theme.breakpoints.down("md")]: {
                                flexDirection: "column",
                            },
                            [theme.breakpoints.up("md")]: {
                                flexDirection: "row",
                            },
                        }}
                    >
                        <Typography variant="h2">{sections[section]?.title ?? sections[section]?.name}</Typography>
                        <Stack direction="row" alignItems="center" gap="16px">
                            {isAssesmentPdfActivated ?
                                <Box>
                                    <Button
                                        variant="primary"
                                        LinkComponent={ssqProgression === 0 ? "button" : "a"}
                                        disabled={ssqProgression === 0}
                                        href={`${getApiUrl()}/api/ssq_posts/${post?.id}/pdf-report`}
                                        target="_blank"
                                        sx={{
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        <FileDownloadOutlinedIcon />
                                        {t("bred.download_report")}
                                    </Button>
                                </Box>
                            :   <></>}

                            {isPdfActivated ?
                                <Box>
                                    <Box>
                                        <Button
                                            variant="ternary"
                                            LinkComponent={exportPdfDisabled ? "button" : "a"}
                                            disabled={exportPdfDisabled}
                                            href={dynamicPdfExportUri}
                                            target="_blank"
                                            endIcon={<FileDownloadOutlinedIcon />}
                                            sx={{
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {t("winepilot.ges.download_report")}
                                        </Button>
                                    </Box>
                                </Box>
                            :   <></>}
                            <ExportSSQExcelButton label={t("ssq.export.excel.button.label")} ssq={ssq} post={post} />
                        </Stack>
                    </Stack>
                )}
                {/* Before deleting this .component below, I just want to make sure all the pages works */}
                <Outlet />
                {/* {sections[section]?.component} */}
            </Stack>
        </Box>
    );
};
